// Make a basic component with Redwing logo at the top and then a block of lorem ipsum

import { Box, Container, Heading, Image, Text } from "@chakra-ui/react";
import usePageMeta from "../../../shared/hooks/usePageMeta";

const Privacy = () => {
  usePageMeta(
    "Privacy Policy - Redwing",
    "How Redwing collects, stores and uses your information.",
    "/privacy"
  );
  return (
    <Container maxW="container.xl">
      <Image src="/images/logo-512.png" maxH="100px" alt="Redwing Logo" />
      <Box>
        <Heading as="h1" mb={6} id="privacy-notice">
          PRIVACY NOTICE
        </Heading>
        <Text mb={4}>
          <strong>Last updated June 28, 2022</strong>
        </Text>
        <Text mb={4}>
          This privacy notice for Redwing Tech Ltd (doing business as Redwing)
          ('<strong>Redwing</strong>', '<strong>we</strong>', '
          <strong>us</strong>', or '<strong>our</strong>',), describes how and
          why we might collect, store, use, and/or share ('
          <strong>process</strong>') your information when you use our services
          ('<strong>Services</strong>'), such as when you:
        </Text>
        <ul>
          <li>
            <Text mb={4}>
              Visit our website at{" "}
              <a href="https://redwing.app">https://redwing.app</a>, or any
              website of ours that links to this privacy notice
            </Text>
          </li>
          <li>
            <Text mb={4}>
              Download and use our mobile application (Redwing), or any other
              application of ours that links to this privacy notice
            </Text>
          </li>
          <li>
            <Text mb={4}>
              Engage with us in other related ways, including any sales,
              marketing, or events
            </Text>
          </li>
        </ul>
        <Text mb={4}>
          <strong>Questions or concerns?</strong> Reading this privacy notice
          will help you understand your privacy rights and choices. If you do
          not agree with our policies and practices, please do not use our
          Services. If you still have any questions or concerns, please contact
          us at contact@redwing.app.
        </Text>
        <Text mb={4}>
          <strong>SUMMARY OF KEY POINTS</strong>
        </Text>
        <Text mb={4}>
          <strong>
            <em>
              This summary provides key points from our privacy notice, but you
              can find out more details about any of these topics by clicking
              the link following each key point or by using our table of
              contents below to find the section you are looking for. You can
              also click
            </em>
          </strong>{" "}
          <a href="#toc">
            <strong>
              <em>here</em>
            </strong>
          </a>{" "}
          <strong>
            <em>to go directly to our table of contents.</em>
          </strong>
        </Text>
        <Text mb={4}>
          <strong>What personal information do we process?</strong> When you
          visit, use, or navigate our Services, we may process personal
          information depending on how you interact with Redwing and the
          Services, the choices you make, and the products and features you use.
          Click <a href="#personalinfo">here</a> to learn more.
        </Text>
        <Text mb={4}>
          <strong>Do we process any sensitive personal information?</strong> We
          do not process sensitive personal information.
        </Text>
        <Text mb={4}>
          <strong>Do we receive any information from third parties?</strong> We
          do not receive any information from third parties.
        </Text>
        <Text mb={4}>
          <strong>How do we process your information?</strong> We process your
          information to provide, improve, and administer our Services,
          communicate with you, for security and fraud prevention, and to comply
          with law. We may also process your information for other purposes with
          your consent. We process your information only when we have a valid
          legal reason to do so. Click <a href="#infouse">here</a> to learn
          more.
        </Text>
        <Text mb={4}>
          <strong>
            In what situations and with which parties do we share personal
            information?
          </strong>{" "}
          We may share information in specific situations and with specific
          third parties. Click <a href="#whoshare">here</a> to learn more.
        </Text>
        <Text mb={4}>
          <strong>How do we keep your information safe?</strong> We have
          organisational and technical processes and procedures in place to
          protect your personal information. However, no electronic transmission
          over the internet or information storage technology can be guaranteed
          to be 100% secure, so we cannot promise or guarantee that hackers,
          cybercriminals, or other unauthorised third parties will not be able
          to defeat our security and improperly collect, access, steal, or
          modify your information. Click <a href="#infosafe">here</a> to learn
          more.
        </Text>
        <Text mb={4}>
          <strong>What are your rights?</strong> Depending on where you are
          located geographically, the applicable privacy law may mean you have
          certain rights regarding your personal information. Click{" "}
          <a href="#privacyrights">here</a> to learn more.
        </Text>
        <Text mb={4}>
          <strong>How do you exercise your rights?</strong> The easiest way to
          exercise your rights is by filling out our data subject request form
          available here: <a href="https://redwing.app">https://redwing.app</a>,
          or by contacting us. We will consider and act upon any request in
          accordance with applicable data protection laws.
        </Text>
        <Text mb={4}>
          Want to learn more about what Redwing does with any information we
          collect? Click <a href="#toc">here</a> to review the notice in full.
        </Text>
        <Text mb={4}>
          <strong>TABLE OF CONTENTS</strong>
        </Text>
        <Text mb={4}>
          <a href="#infocollect">1. WHAT INFORMATION DO WE COLLECT?</a>
        </Text>
        <Text mb={4}>
          <a href="#infouse">2. HOW DO WE PROCESS YOUR INFORMATION?</a>
        </Text>
        <Text mb={4}>
          <a href="#legalbases">
            3. WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR PERSONAL
            INFORMATION?
          </a>
        </Text>
        <Text mb={4}>
          <a href="#whoshare">
            4. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
          </a>
        </Text>
        <Text mb={4}>
          <a href="#3pwebsites">
            5. WHAT IS OUR STANCE ON THIRD-PARTY WEBSITES?
          </a>
        </Text>
        <Text mb={4}>
          <a href="#cookies">
            6. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?
          </a>
        </Text>
        <Text mb={4}>
          <a href="#sociallogins">7. HOW DO WE HANDLE YOUR SOCIAL LOGINS?</a>
        </Text>
        <Text mb={4}>
          <a href="#inforetain">8. HOW LONG DO WE KEEP YOUR INFORMATION?</a>
        </Text>
        <Text mb={4}>
          <a href="#infosafe">9. HOW DO WE KEEP YOUR INFORMATION SAFE?</a>
        </Text>
        <Text mb={4}>
          <a href="#privacyrights">10. WHAT ARE YOUR PRIVACY RIGHTS?</a>
        </Text>
        <Text mb={4}>
          <a href="#DNT">11. CONTROLS FOR DO-NOT-TRACK FEATURES</a>
        </Text>
        <Text mb={4}>
          <a href="#caresidents">
            12. DO CALIFORNIA RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?
          </a>
        </Text>
        <Text mb={4}>
          <a href="#policyupdates">13. DO WE MAKE UPDATES TO THIS NOTICE?</a>
        </Text>
        <Text mb={4}>
          <a href="#contact">14. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</a>
        </Text>
        <Text mb={4}>
          <a href="#request">
            15. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM
            YOU?
          </a>
        </Text>
        <Heading as="h2" mb={4}>
          <strong>1. WHAT INFORMATION DO WE COLLECT?</strong>
        </Heading>
        <Text mb={4}>
          <strong>Personal information you disclose to us</strong>
        </Text>
        <Text mb={4}>
          <strong>
            <em>In Short:</em>
          </strong>{" "}
          <em>We collect personal information that you provide to us.</em>
        </Text>
        <Text mb={4}>
          We collect personal information that you voluntarily provide to us
          when you register on the Services, express an interest in obtaining
          information about us or our products and Services, when you
          participate in activities on the Services, or otherwise when you
          contact us.
        </Text>
        <Text mb={4}>
          <strong>Personal Information Provided by You.</strong> The personal
          information that we collect depends on the context of your
          interactions with us and the Services, the choices you make, and the
          products and features you use. The personal information we collect may
          include the following:
        </Text>
        <ul>
          <li>names</li>
          <li>email addresses</li>
          <li>usernames</li>
          <li>passwords</li>
        </ul>
        <Text mb={4}>
          <strong>Sensitive Information.</strong> We do not process sensitive
          information.
        </Text>
        <Text mb={4}>
          <strong>Payment Data.</strong> We may collect data necessary to
          process your payment if you make purchases, such as your payment
          instrument number (such as a credit card number), and the security
          code associated with your payment instrument. All payment data is
          stored by Stripe. You may find their privacy notice link(s) here:{" "}
          <a href="https://stripe.com/gb/privacy">
            https://stripe.com/gb/privacy
          </a>
          .
        </Text>
        <Text mb={4}>
          <strong>Social Media Login Data.</strong> We may provide you with the
          option to register with us using your existing social media account
          details, like your Facebook, Twitter, or other social media account.
          If you choose to register in this way, we will collect the information
          described in the section called '
          <a href="#sociallogins">HOW DO WE HANDLE YOUR SOCIAL LOGINS?</a>'
          below.
        </Text>
        <Text mb={4}>
          <strong>Application Data.</strong> If you use our application(s), we
          also may collect the following information if you choose to provide us
          with access or permission:
        </Text>
        <ul>
          <li>
            <Text mb={4}>
              <em>Mobile Device Data.</em> We automatically collect device
              information (such as your mobile device ID, model, and
              manufacturer), operating system, version information and system
              configuration information, device and application identification
              numbers, browser type and version, hardware model Internet service
              provider and/or mobile carrier, and Internet Protocol (IP) address
              (or proxy server). If you are using our application(s), we may
              also collect information about the phone network associated with
              your mobile device, your mobile device's operating system or
              platform, the type of mobile device you use, your mobile device's
              unique device ID, and information about the features of our
              application(s) you accessed.
            </Text>
          </li>
          <li>
            <Text mb={4}>
              <em>Push Notifications.</em> We may request to send you push
              notifications regarding your account or certain features of the
              application(s). If you wish to opt out from receiving these types
              of communications, you may turn them off in your device's
              settings.
            </Text>
          </li>
        </ul>
        <Text mb={4}>
          This information is primarily needed to maintain the security and
          operation of our application(s), for troubleshooting, and for our
          internal analytics and reporting purposes.
        </Text>
        <Text mb={4}>
          All personal information that you provide to us must be true,
          complete, and accurate, and you must notify us of any changes to such
          personal information.
        </Text>
        <Text mb={4}>
          <strong>Information automatically collected</strong>
        </Text>
        <Text mb={4}>
          <strong>
            <em>In Short:</em>
          </strong>{" "}
          <em>
            Some information — such as your Internet Protocol (IP) address
            and/or browser and device characteristics — is collected
            automatically when you visit our Services.
          </em>
        </Text>
        <Text mb={4}>
          We automatically collect certain information when you visit, use, or
          navigate the Services. This information does not reveal your specific
          identity (like your name or contact information) but may include
          device and usage information, such as your IP address, browser and
          device characteristics, operating system, language preferences,
          referring URLs, device name, country, location, information about how
          and when you use our Services, and other technical information. This
          information is primarily needed to maintain the security and operation
          of our Services, and for our internal analytics and reporting
          purposes.
        </Text>
        <Text mb={4}>
          Like many businesses, we also collect information through cookies and
          similar technologies.
        </Text>
        <Text mb={4}>The information we collect includes:</Text>
        <ul>
          <li>
            <Text mb={4}>
              <em>Log and Usage Data.</em> Log and usage data is
              service-related, diagnostic, usage, and performance information
              our servers automatically collect when you access or use our
              Services and which we record in log files. Depending on how you
              interact with us, this log data may include your IP address,
              device information, browser type, and settings and information
              about your activity in the Services (such as the date/time stamps
              associated with your usage, pages and files viewed, searches, and
              other actions you take such as which features you use), device
              event information (such as system activity, error reports
              (sometimes called 'crash dumps'), and hardware settings).
            </Text>
          </li>
        </ul>
        <Text mb={4}>
          <strong>2. HOW DO WE PROCESS YOUR INFORMATION?</strong>
        </Text>
        <Text mb={4}>
          <strong>
            <em>In Short:</em>
          </strong>{" "}
          <em>
            We process your information to provide, improve, and administer our
            Services, communicate with you, for security and fraud prevention,
            and to comply with law. We may also process your information for
            other purposes with your consent.
          </em>
        </Text>
        <Text mb={4}>
          <strong>
            We process your personal information for a variety of reasons,
            depending on how you interact with our Services, including:
          </strong>
        </Text>
        <ul>
          <li>
            <Text mb={4}>
              <strong>
                To facilitate account creation and authentication and otherwise
                manage user accounts.
              </strong>{" "}
              We may process your information so you can create and log in to
              your account, as well as keep your account in working order.
            </Text>
          </li>
          <li>
            <Text mb={4}>
              <strong>
                To deliver and facilitate delivery of services to the user.
              </strong>{" "}
              We may process your information to provide you with the requested
              service.
            </Text>
          </li>
          <li>
            <Text mb={4}>
              <strong>
                To respond to user inquiries/offer support to users.
              </strong>{" "}
              We may process your information to respond to your inquiries and
              solve any potential issues you might have with the requested
              service.
            </Text>
          </li>
          <li>
            <Text mb={4}>
              <strong>To send administrative information to you.</strong> We may
              process your information to send you details about our products
              and services, changes to our terms and policies, and other similar
              information.
            </Text>
          </li>
          <li>
            <Text mb={4}>
              <strong>To fulfil and manage your orders.</strong> We may process
              your information to fulfil and manage your orders, payments,
              returns, and exchanges made through the Services.
            </Text>
          </li>
          <li>
            <Text mb={4}>
              <strong>
                To save or protect an individual's vital interest.
              </strong>{" "}
              We may process your information when necessary to save or protect
              an individual's vital interest, such as to prevent harm.
            </Text>
          </li>
        </ul>
        <Text mb={4}>
          <strong>
            3. WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR INFORMATION?
          </strong>
        </Text>
        <Text mb={4}>
          <em>
            <strong>In Short:</strong> We only process your personal information
            when we believe it is necessary and we have a valid legal reason
            (i.e. legal basis) to do so under applicable law, like with your
            consent, to comply with laws, to provide you with services to enter
            into or fulfil our contractual obligations, to protect your rights,
            or to fulfil our legitimate business interests.
          </em>
        </Text>
        <Text mb={4}>
          <em>
            <strong>
              If you are located in the EU or UK, this section applies to you.
            </strong>
          </em>
        </Text>
        <Text mb={4}>
          The General Data Protection Regulation (GDPR) and UK GDPR require us
          to explain the valid legal bases we rely on in order to process your
          personal information. As such, we may rely on the following legal
          bases to process your personal information:
        </Text>
        <ul>
          <li>
            <Text mb={4}>
              <strong>Consent.</strong> We may process your information if you
              have given us permission (i.e. consent) to use your personal
              information for a specific purpose. You can withdraw your consent
              at any time. Click <a href="#withdrawconsent">here</a> to learn
              more.
            </Text>
          </li>
          <li>
            <Text mb={4}>
              <strong>Performance of a Contract.</strong> We may process your
              personal information when we believe it is necessary to fulfil our
              contractual obligations to you, including providing our Services
              or at your request prior to entering into a contract with you.
            </Text>
          </li>
          <li>
            <Text mb={4}>
              <strong>Legal Obligations.</strong> We may process your
              information where we believe it is necessary for compliance with
              our legal obligations, such as to cooperate with a law enforcement
              body or regulatory agency, exercise or defend our legal rights, or
              disclose your information as evidence in litigation in which we
              are involved.
            </Text>
          </li>
          <li>
            <Text mb={4}>
              <strong>Vital Interests.</strong> We may process your information
              where we believe it is necessary to protect your vital interests
              or the vital interests of a third party, such as situations
              involving potential threats to the safety of any person.
            </Text>
          </li>
        </ul>
        <Text mb={4}>
          In legal terms, we are generally the 'data controller' under European
          data protection laws of the personal information described in this
          privacy notice, since we determine the means and/or purposes of the
          data processing we perform. This privacy notice does not apply to the
          personal information we process as a 'data processor' on behalf of our
          customers. In those situations, the customer that we provide services
          to and with whom we have entered into a data processing agreement is
          the 'data controller' responsible for your personal information, and
          we merely process your information on their behalf in accordance with
          your instructions. If you want to know more about our customers'
          privacy practices, you should read their privacy policies and direct
          any questions you have to them.
        </Text>
        <Text mb={4}>
          <em>
            <strong>
              If you are located in Canada, this section applies to you.
            </strong>
          </em>
        </Text>
        <Text mb={4}>
          We may process your information if you have given us specific
          permission (i.e. express consent) to use your personal information for
          a specific purpose, or in situations where your permission can be
          inferred (i.e. implied consent). You can withdraw your consent at any
          time. Click <a href="#withdrawconsent">here</a> to learn more.
        </Text>
        <Text mb={4}>
          In some exceptional cases, we may be legally permitted under
          applicable law to process your information without your consent,
          including, for example:
        </Text>
        <ul>
          <li>
            <Text mb={4}>
              If collection is clearly in the interests of an individual and
              consent cannot be obtained in a timely way
            </Text>
          </li>
          <li>
            <Text mb={4}>
              For investigations and fraud detection and prevention
            </Text>
          </li>
          <li>
            <Text mb={4}>
              For business transactions provided certain conditions are met
            </Text>
          </li>
          <li>
            <Text mb={4}>
              If it is contained in a witness statement and the collection is
              necessary to assess, process, or settle an insurance claim
            </Text>
          </li>
          <li>
            <Text mb={4}>
              For identifying injured, ill, or deceased persons and
              communicating with next of kin
            </Text>
          </li>
          <li>
            <Text mb={4}>
              If we have reasonable grounds to believe an individual has been,
              is, or may be victim of financial abuse
            </Text>
          </li>
          <li>
            <Text mb={4}>
              If it is reasonable to expect collection and use with consent
              would compromise the availability or the accuracy of the
              information and the collection is reasonable for purposes related
              to investigating a breach of an agreement or a contravention of
              the laws of Canada or a province
            </Text>
          </li>
          <li>
            <Text mb={4}>
              If disclosure is required to comply with a subpoena, warrant,
              court order, or rules of the court relating to the production of
              records
            </Text>
          </li>
          <li>
            <Text mb={4}>
              If it was produced by an individual in the course of their
              employment, business, or profession and the collection is
              consistent with the purposes for which the information was
              produced
            </Text>
          </li>
          <li>
            <Text mb={4}>
              If the collection is solely for journalistic, artistic, or
              literary purposes
            </Text>
          </li>
          <li>
            <Text mb={4}>
              If the information is publicly available and is specified by the
              regulations
            </Text>
          </li>
        </ul>
        <Text mb={4}>
          <strong>
            4. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
          </strong>
        </Text>
        <Text mb={4}>
          <strong>
            <em>In Short:</em>
          </strong>{" "}
          <em>
            We may share information in specific situations described in this
            section and/or with the following third parties.
          </em>
        </Text>
        <Text mb={4}>
          We may need to share your personal information in the following
          situations:
        </Text>
        <ul>
          <li>
            <Text mb={4}>
              <strong>Business Transfers.</strong> We may share or transfer your
              information in connection with, or during negotiations of, any
              merger, sale of company assets, financing, or acquisition of all
              or a portion of our business to another company.
            </Text>
          </li>
          <li>
            <Text mb={4}>
              <strong>Offer Wall.</strong> Our application(s) may display a
              third-party hosted 'offer wall'. Such an offer wall allows
              third-party advertisers to offer virtual currency, gifts, or other
              items to users in return for the acceptance and completion of an
              advertisement offer. Such an offer wall may appear in our
              application(s) and be displayed to you based on certain data, such
              as your geographic area or demographic information. When you click
              on an offer wall, you will be brought to an external website
              belonging to other persons and will leave our application(s). A
              unique identifier, such as your user ID, will be shared with the
              offer wall provider in order to prevent fraud and properly credit
              your account with the relevant reward.
            </Text>
          </li>
        </ul>
        <Text mb={4}>
          <strong>5. WHAT IS OUR STANCE ON THIRD-PARTY WEBSITES?</strong>
        </Text>
        <Text mb={4}>
          <strong>
            <em>In Short:</em>
          </strong>{" "}
          <em>
            We are not responsible for the safety of any information that you
            share with third parties that we may link to or who advertise on our
            Services, but are not affiliated with, our Services.
          </em>
        </Text>
        <Text mb={4}>
          The Services, including our offer wall, may link to third-party
          websites, online services, or mobile applications and/or contain
          advertisements from third parties that are not affiliated with us and
          which may link to other websites, services, or applications.
          Accordingly, we do not make any guarantee regarding any such third
          parties, and we will not be liable for any loss or damage caused by
          the use of such third-party websites, services, or applications. The
          inclusion of a link towards a third-party website, service, or
          application does not imply an endorsement by us. We cannot guarantee
          the safety and privacy of data you provide to any third parties. Any
          data collected by third parties is not covered by this privacy notice.
          We are not responsible for the content or privacy and security
          practices and policies of any third parties, including other websites,
          services, or applications that may be linked to or from the Services.
          You should review the policies of such third parties and contact them
          directly to respond to your questions.
        </Text>
        <Text mb={4}>
          <strong>6. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?</strong>
        </Text>
        <Text mb={4}>
          <strong>
            <em>In Short:</em>
          </strong>{" "}
          <em>
            We may use cookies and other tracking technologies to collect and
            store your information.
          </em>
        </Text>
        <Text mb={4}>
          We may use cookies and similar tracking technologies (like web beacons
          and pixels) to access or store information. Specific information about
          how we use such technologies and how you can refuse certain cookies is
          set out in our Cookie Notice.
        </Text>
        <Text mb={4}>
          <strong>7. HOW DO WE HANDLE YOUR SOCIAL LOGINS?</strong>
        </Text>
        <Text mb={4}>
          <strong>
            <em>In Short:</em>
          </strong>{" "}
          <em>
            If you choose to register or log in to our services using a social
            media account, we may have access to certain information about you.
          </em>
        </Text>
        <Text mb={4}>
          Our Services offer you the ability to register and log in using your
          third-party social media account details (like your Facebook or
          Twitter logins). Where you choose to do this, we will receive certain
          profile information about you from your social media provider. The
          profile information we receive may vary depending on the social media
          provider concerned, but will often include your name, email address,
          friends list, and profile picture, as well as other information you
          choose to make public on such a social media platform.
        </Text>
        <Text mb={4}>
          We will use the information we receive only for the purposes that are
          described in this privacy notice or that are otherwise made clear to
          you on the relevant Services. Please note that we do not control, and
          are not responsible for, other uses of your personal information by
          your third-party social media provider. We recommend that you review
          their privacy notice to understand how they collect, use, and share
          your personal information, and how you can set your privacy
          preferences on their sites and apps.
        </Text>
        <Text mb={4}>
          <strong>8. HOW LONG DO WE KEEP YOUR INFORMATION?</strong>
        </Text>
        <Text mb={4}>
          <strong>
            <em>In Short:</em>
          </strong>{" "}
          <em>
            We keep your information for as long as necessary to fulfil the
            purposes outlined in this privacy notice unless otherwise required
            by law.
          </em>
        </Text>
        <Text mb={4}>
          We will only keep your personal information for as long as it is
          necessary for the purposes set out in this privacy notice, unless a
          longer retention period is required or permitted by law (such as tax,
          accounting, or other legal requirements). No purpose in this notice
          will require us keeping your personal information for longer than the
          period of time in which users have an account with us.
        </Text>
        <Text mb={4}>
          When we have no ongoing legitimate business need to process your
          personal information, we will either delete or anonymise such
          information, or, if this is not possible (for example, because your
          personal information has been stored in backup archives), then we will
          securely store your personal information and isolate it from any
          further processing until deletion is possible.
        </Text>
        <Text mb={4}>
          <strong>9. HOW DO WE KEEP YOUR INFORMATION SAFE?</strong>
        </Text>
        <Text mb={4}>
          <strong>
            <em>In Short:</em>
          </strong>{" "}
          <em>
            We aim to protect your personal information through a system of
            organisational and technical security measures.
          </em>
        </Text>
        <Text mb={4}>
          We have implemented appropriate and reasonable technical and
          organisational security measures designed to protect the security of
          any personal information we process. However, despite our safeguards
          and efforts to secure your information, no electronic transmission
          over the Internet or information storage technology can be guaranteed
          to be 100% secure, so we cannot promise or guarantee that hackers,
          cybercriminals, or other unauthorised third parties will not be able
          to defeat our security and improperly collect, access, steal, or
          modify your information. Although we will do our best to protect your
          personal information, transmission of personal information to and from
          our Services is at your own risk. You should only access the Services
          within a secure environment.
        </Text>
        <Text mb={4}>
          <strong>10. WHAT ARE YOUR PRIVACY RIGHTS?</strong>
        </Text>
        <Text mb={4}>
          <strong>
            <em>In Short:</em>
          </strong>{" "}
          <em>
            In some regions, such as the European Economic Area (EEA), United
            Kingdom (UK), and Canada, you have rights that allow you greater
            access to and control over your personal information. You may
            review, change, or terminate your account at any time.
          </em>
        </Text>
        <Text mb={4}>
          In some regions (like the EEA, UK, and Canada), you have certain
          rights under applicable data protection laws. These may include the
          right (i) to request access and obtain a copy of your personal
          information, (ii) to request rectification or erasure; (iii) to
          restrict the processing of your personal information; and (iv) if
          applicable, to data portability. In certain circumstances, you may
          also have the right to object to the processing of your personal
          information. You can make such a request by contacting us by using the
          contact details provided in the section '
          <a href="#contact">HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</a>'
          below.
        </Text>
        <Text mb={4}>
          We will consider and act upon any request in accordance with
          applicable data protection laws.
        </Text>
        <Text mb={4}>
          If you are located in the EEA or UK and you believe we are unlawfully
          processing your personal information, you also have the right to
          complain to your local data protection supervisory authority. You can
          find their contact details here:{" "}
          <a href="https://ec.europa.eu/justice/data-protection/bodies/authorities/index_en.htm">
            https://ec.europa.eu/justice/data-protection/bodies/authorities/index_en.htm
          </a>
          .
        </Text>
        <Text mb={4}>
          If you are located in Switzerland, the contact details for the data
          protection authorities are available here:{" "}
          <a href="https://www.edoeb.admin.ch/edoeb/en/home.html">
            https://www.edoeb.admin.ch/edoeb/en/home.html
          </a>
          .
        </Text>
        <Text mb={4}>
          <strong>Withdrawing your consent:</strong> If we are relying on your
          consent to process your personal information, which may be express
          and/or implied consent depending on the applicable law, you have the
          right to withdraw your consent at any time. You can withdraw your
          consent at any time by contacting us by using the contact details
          provided in the section '
          <a href="#contact">HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</a>'
          below.
        </Text>
        <Text mb={4}>
          However, please note that this will not affect the lawfulness of the
          processing before its withdrawal, nor when applicable law allows, will
          it affect the processing of your personal information conducted in
          reliance on lawful processing grounds other than consent.
        </Text>
        <Text mb={4}>
          <strong>
            Opting out of marketing and promotional communications:
          </strong>{" "}
          You can unsubscribe from our marketing and promotional communications
          at any time by clicking on the unsubscribe link in the emails that we
          send, or by contacting us using the details provided in the section '
          <a href="#contact">HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</a>'
          below. You will then be removed from the marketing lists. However, we
          may still communicate with you — for example, to send you
          service-related messages that are necessary for the administration and
          use of your account, to respond to service requests, or for other
          non-marketing purposes.
        </Text>
        <Text mb={4}>
          <strong>Account Information</strong>
        </Text>
        <Text mb={4}>
          If you would at any time like to review or change the information in
          your account or terminate your account, you can:
        </Text>
        <ul>
          <li>Contact us using the contact information provided.</li>
        </ul>
        <Text mb={4}>
          Upon your request to terminate your account, we will deactivate or
          delete your account and information from our active databases.
          However, we may retain some information in our files to prevent fraud,
          troubleshoot problems, assist with any investigations, enforce our
          legal terms and/or comply with applicable legal requirements.
        </Text>
        <Text mb={4}>
          <strong>Cookies and similar technologies:</strong> Most Web browsers
          are set to accept cookies by default. If you prefer, you can usually
          choose to set your browser to remove cookies and to reject cookies. If
          you choose to remove cookies or reject cookies, this could affect
          certain features or services of our Services. To opt out of
          interest-based advertising by advertisers on our Services visit{" "}
          <a href="http://www.aboutads.info/choices/">
            http://www.aboutads.info/choices/
          </a>
          .
        </Text>
        <Text mb={4}>
          If you have questions or comments about your privacy rights, you may
          email us at contact@redwing.app.
        </Text>
        <Text mb={4}>
          <strong>11. CONTROLS FOR DO-NOT-TRACK FEATURES</strong>
        </Text>
        <Text mb={4}>
          Most web browsers and some mobile operating systems and mobile
          applications include a Do-Not-Track ('DNT') feature or setting you can
          activate to signal your privacy preference not to have data about your
          online browsing activities monitored and collected. At this stage no
          uniform technology standard for recognising and implementing DNT
          signals has been finalised. As such, we do not currently respond to
          DNT browser signals or any other mechanism that automatically
          communicates your choice not to be tracked online. If a standard for
          online tracking is adopted that we must follow in the future, we will
          inform you about that practice in a revised version of this privacy
          notice.
        </Text>
        <Text mb={4}>
          <strong>
            12. DO CALIFORNIA RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?
          </strong>
        </Text>
        <Text mb={4}>
          <strong>
            <em>In Short:</em>
          </strong>{" "}
          <em>
            Yes, if you are a resident of California, you are granted specific
            rights regarding access to your personal information.
          </em>
        </Text>
        <Text mb={4}>
          California Civil Code Section 1798.83, also known as the 'Shine The
          Light' law, permits our users who are California residents to request
          and obtain from us, once a year and free of charge, information about
          categories of personal information (if any) we disclosed to third
          parties for direct marketing purposes and the names and addresses of
          all third parties with which we shared personal information in the
          immediately preceding calendar year. If you are a California resident
          and would like to make such a request, please submit your request in
          writing to us using the contact information provided below.
        </Text>
        <Text mb={4}>
          If you are under 18 years of age, reside in California, and have a
          registered account with Services, you have the right to request
          removal of unwanted data that you publicly post on the Services. To
          request removal of such data, please contact us using the contact
          information provided below and include the email address associated
          with your account and a statement that you reside in California. We
          will make sure the data is not publicly displayed on the Services, but
          please be aware that the data may not be completely or comprehensively
          removed from all our systems (e.g. backups, etc.).
        </Text>
        <Text mb={4}>
          <strong>13. DO WE MAKE UPDATES TO THIS NOTICE?</strong>
        </Text>
        <Text mb={4}>
          <em>
            <strong>In Short:</strong> Yes, we will update this notice as
            necessary to stay compliant with relevant laws.
          </em>
        </Text>
        <Text mb={4}>
          We may update this privacy notice from time to time. The updated
          version will be indicated by an updated 'Revised' date and the updated
          version will be effective as soon as it is accessible. If we make
          material changes to this privacy notice, we may notify you either by
          prominently posting a notice of such changes or by directly sending
          you a notification. We encourage you to review this privacy notice
          frequently to be informed of how we are protecting your information.
        </Text>
        <Text mb={4}>
          <strong>14. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</strong>
        </Text>
        <Text mb={4}>
          If you have questions or comments about this notice, you may email us
          at contact@redwing.app or by post to:
        </Text>
        <Text mb={4}>Redwing Tech Ltd</Text>
        <Text mb={4}>32 Christiemiller avenue</Text>
        <Text mb={4}>Edinburgh EH7 6ST</Text>
        <Text mb={4}>United Kingdom</Text>
        <Text mb={4}>
          If you are a resident in the United Kingdom, the 'data controller' of
          your personal information is Redwing Tech Ltd. Redwing Tech Ltd has
          appointed Thomas Quiroga to be its representative in the UK. You can
          contact them directly regarding the processing of your information by
          Redwing Tech Ltd, by email at thomas@redwing.app, by visiting{" "}
          <a href="https://redwing.app">https://redwing.app</a>, by phone at
          +447577248802, or by post to:
        </Text>
        <Text mb={4}>32 Christiemiller avenue</Text>
        <Text mb={4}>Edinburgh, Scotland EH7 6ST</Text>
        <Text mb={4}>Scotland</Text>
        <Text mb={4}>
          <strong>
            15. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM
            YOU?
          </strong>
        </Text>
        <Text mb={4}>
          Based on the applicable laws of your country, you may have the right
          to request access to the personal information we collect from you,
          change that information, or delete it. To request to review, update,
          or delete your personal information, please visit:{" "}
          <a href="https://redwing.app">https://redwing.app</a>
        </Text>
      </Box>
    </Container>
  );
};

export default Privacy;
